import React, { useState } from 'react';
import { Phone, Mail, Calendar, CheckCircle, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LogoOption1 } from '../components/LogoOptions';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export const GetQuote = () => {
  const [formData, setFormData] = useState({
    borrowerFirstName: '',
    borrowerLastName: '',
    borrowerDOB: '',
    borrowerEmail: '',
    borrowerPhone: '',
    coBorrowerFirstName: '',
    coBorrowerLastName: '',
    coBorrowerDOB: '',
    monthlyIncome: '',
    totalAssets: '',
    monthlyHousingExpenses: '',
    monthlyDebtExpenses: '',
    householdSize: '',
    creditEvaluation: '',
    propertyAddress: '',
    squareFootage: '',
    propertyType: '',
    marketValue: '',
    firstMortgageBalance: '',
    secondMortgageBalance: '',
    purpose: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/quote-request`, formData);
      // Redirect to thank you page for conversion tracking
      window.location.href = '/thank-you';
    } catch (error) {
      // Error logged silently - user sees alert
      alert('Failed to submit. Please call us directly at (720) 307-8711');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Breadcrumb */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 py-3 text-sm">
            <Link 
              to="/" 
              className="flex items-center text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              <Home className="w-4 h-4 mr-1" />
              Home
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-600 font-medium">Get a Quote</span>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="relative pt-8 pb-8 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center mb-6">
            <LogoOption1 className="h-16" />
          </div>
          
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Get Your Personalized Reverse Mortgage Analysis
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              As a CPA and Licensed Mortgage Specialist, I'll provide a comprehensive analysis of your situation—including tax implications, cash flow impact, and wealth preservation strategies.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="border-2 border-blue-200 shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Borrower Information */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                    Borrower Information
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="borrowerFirstName">First Name *</Label>
                      <Input
                        id="borrowerFirstName"
                        name="borrowerFirstName"
                        value={formData.borrowerFirstName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="borrowerLastName">Last Name *</Label>
                      <Input
                        id="borrowerLastName"
                        name="borrowerLastName"
                        value={formData.borrowerLastName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="borrowerDOB">Date of Birth *</Label>
                      <Input
                        id="borrowerDOB"
                        name="borrowerDOB"
                        type="date"
                        value={formData.borrowerDOB}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="borrowerEmail">Email *</Label>
                      <Input
                        id="borrowerEmail"
                        name="borrowerEmail"
                        type="email"
                        value={formData.borrowerEmail}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="borrowerPhone">Phone Number *</Label>
                      <Input
                        id="borrowerPhone"
                        name="borrowerPhone"
                        type="tel"
                        value={formData.borrowerPhone}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Co-Borrower Information */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                    Co-Borrower Information <span className="text-sm font-normal text-gray-500">(Optional)</span>
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="coBorrowerFirstName">First Name</Label>
                      <Input
                        id="coBorrowerFirstName"
                        name="coBorrowerFirstName"
                        value={formData.coBorrowerFirstName}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="coBorrowerLastName">Last Name</Label>
                      <Input
                        id="coBorrowerLastName"
                        name="coBorrowerLastName"
                        value={formData.coBorrowerLastName}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="coBorrowerDOB">Date of Birth</Label>
                      <Input
                        id="coBorrowerDOB"
                        name="coBorrowerDOB"
                        type="date"
                        value={formData.coBorrowerDOB}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Financial Information */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                    Financial Information
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="monthlyIncome">Monthly Household Income *</Label>
                      <Input
                        id="monthlyIncome"
                        name="monthlyIncome"
                        type="number"
                        placeholder="$5,000"
                        value={formData.monthlyIncome}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="totalAssets">Total Assets *</Label>
                      <Input
                        id="totalAssets"
                        name="totalAssets"
                        type="number"
                        placeholder="$200,000"
                        value={formData.totalAssets}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="monthlyHousingExpenses">Monthly Housing Expenses *</Label>
                      <Input
                        id="monthlyHousingExpenses"
                        name="monthlyHousingExpenses"
                        type="number"
                        placeholder="$2,000"
                        value={formData.monthlyHousingExpenses}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="monthlyDebtExpenses">Other Monthly Debt *</Label>
                      <Input
                        id="monthlyDebtExpenses"
                        name="monthlyDebtExpenses"
                        type="number"
                        placeholder="$500"
                        value={formData.monthlyDebtExpenses}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="householdSize">Household Size *</Label>
                      <Input
                        id="householdSize"
                        name="householdSize"
                        type="number"
                        placeholder="2"
                        value={formData.householdSize}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="creditEvaluation">Credit Evaluation *</Label>
                      <Select 
                        value={formData.creditEvaluation}
                        onValueChange={(value) => handleSelectChange('creditEvaluation', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="excellent">Excellent (720+)</SelectItem>
                          <SelectItem value="good">Good (680-719)</SelectItem>
                          <SelectItem value="fair">Fair (640-679)</SelectItem>
                          <SelectItem value="poor">Poor (&lt;640)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Property Information */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                    Property Information
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor="propertyAddress">Property Address *</Label>
                      <Input
                        id="propertyAddress"
                        name="propertyAddress"
                        placeholder="123 Main St, Denver, CO 80202"
                        value={formData.propertyAddress}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="squareFootage">Approximate Square Footage *</Label>
                      <Select 
                        value={formData.squareFootage}
                        onValueChange={(value) => handleSelectChange('squareFootage', value)}
                      >
                        <SelectTrigger id="squareFootage">
                          <SelectValue placeholder="Select range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="under-1500">Under 1,500 sq ft</SelectItem>
                          <SelectItem value="1500-2500">1,501 – 2,500 sq ft</SelectItem>
                          <SelectItem value="2501-3500">2,501 – 3,500 sq ft</SelectItem>
                          <SelectItem value="over-3500">Over 3,500 sq ft</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="propertyType">Property Type *</Label>
                      <Select 
                        value={formData.propertyType}
                        onValueChange={(value) => handleSelectChange('propertyType', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sfh">Single-Family Home</SelectItem>
                          <SelectItem value="condo">Condo/Townhome</SelectItem>
                          <SelectItem value="manufactured">Manufactured Home</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="marketValue">Estimated Market Value *</Label>
                      <Input
                        id="marketValue"
                        name="marketValue"
                        type="number"
                        placeholder="$500,000"
                        value={formData.marketValue}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="firstMortgageBalance">1st Mortgage Balance *</Label>
                      <Input
                        id="firstMortgageBalance"
                        name="firstMortgageBalance"
                        type="number"
                        placeholder="$200,000 (or $0 if paid off)"
                        value={formData.firstMortgageBalance}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="secondMortgageBalance">2nd Mortgage Balance</Label>
                      <Input
                        id="secondMortgageBalance"
                        name="secondMortgageBalance"
                        type="number"
                        placeholder="$0"
                        value={formData.secondMortgageBalance}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Purpose & Message */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                    Your Goals
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="purpose">Primary Purpose *</Label>
                      <Select 
                        value={formData.purpose}
                        onValueChange={(value) => handleSelectChange('purpose', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select your main goal..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="eliminate_mortgage">Eliminate Monthly Mortgage Payment</SelectItem>
                          <SelectItem value="supplement_income">Supplement Retirement Income</SelectItem>
                          <SelectItem value="home_improvements">Pay for Home Improvements</SelectItem>
                          <SelectItem value="healthcare">Cover Healthcare Expenses</SelectItem>
                          <SelectItem value="debt_consolidation">Pay Off Debt</SelectItem>
                          <SelectItem value="delay_social_security">Delay Social Security</SelectItem>
                          <SelectItem value="estate_planning">Estate/Tax Planning</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="message">Additional Details or Questions</Label>
                      <Textarea
                        id="message"
                        name="message"
                        rows={4}
                        placeholder="Tell me more about your situation or any specific questions you have..."
                        value={formData.message}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6 border-t border-gray-200">
                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      'Submitting...'
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Get My Personalized Analysis
                      </>
                    )}
                  </Button>
                  <p className="text-sm text-gray-500 text-center mt-4">
                    By submitting, you agree to be contacted about reverse mortgage options.
                    I&apos;ll typically respond within 24 hours.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Contact Info Below Form */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">
              <strong>Prefer to talk now?</strong>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="outline"
                size="lg"
                onClick={() => window.location.href = 'tel:+17203078711'}
              >
                <Phone className="w-4 h-4 mr-2" />
                Call (720) 307-8711
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => window.open('https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ3-Q5PB32iYcYOFy0AfHcJQEewh-0EC12gx8qLVXkzCXMl3Ok1RTHDMZLWUv-lyg1PdwLj-W9SR', '_blank')}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Schedule a Call
              </Button>
            </div>
          </div>

          {/* Trust Footer */}
          <div className="text-center mt-8 text-sm text-gray-600">
            <p>Peter Andrew S., CPA & Licensed Mortgage Specialist | NMLS #2575736</p>
            <p className="mt-1">Serving Colorado homeowners 62+</p>
          </div>
        </div>
      </section>
    </div>
  );
};
